import {type ReactNode, useState} from 'react';
import {
  BadgeCheck,
  Bookmark,
  ChevronRight,
  Clock,
  Eye,
  Film,
  type LucideIcon,
  MessageSquare,
  Store,
  ThumbsUp,
  Users
} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {TranslateToggle} from '@/components/shared/TranslateToggle';
import {useTranslatedText, useTranslatedTexts} from '@/store/translationStore';
import {
  getDescription,
  getMetaFieldAny,
  getMetaBoolean,
  getMetaFields,
  getTags,
  formatCompactNumber,
  type ItemMetadataLike,
  DURATION_FIELD_NAMES,
  VIEW_COUNT_FIELD_NAMES,
  LIKE_COUNT_FIELD_NAMES,
  COMMENT_COUNT_FIELD_NAMES,
  CATEGORY_FIELD_NAMES,
  SAVE_COUNT_FIELD_NAMES,
  FOLLOWER_FIELD_NAMES,
  VERIFIED_FIELD_NAMES,
  VERIFIED_MERCHANT_FIELD_NAMES,
  LANGUAGE_FIELD_NAMES,
  MADE_FOR_KIDS_FIELD_NAMES,
  SAFE_FOR_MINORS_FIELD_NAMES,
  LIVESTREAM_FIELD_NAMES,
  MATURE_FIELD_NAMES,
  KNOWN_FIELD_NAMES,
  SUBSCRIBER_FIELD_NAMES,
  CHANNEL_VIDEO_COUNT_FIELD_NAMES,
  LAST_POST_DATE_FIELD_NAMES,
  JOINED_DATE_FIELD_NAMES,
  COUNTRY_FIELD_NAMES
} from '@/lib/itemMetadata';
import {VERIFIED_LABEL} from '@/lib/platform';
import {cn} from '@/lib/utils';

// A key-stat chip: the icon carries the label for assistive tech (so the chip reads "Followers
// 125K"), and the tooltip spells it out for a sighted analyst on hover, since the icon alone is
// the only visible hint of what the number counts.
function StatChip({
  label,
  icon: Icon,
  variant = 'outline',
  children
}: {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly variant?: 'outline' | 'tag';
  readonly children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Badge variant={variant} />}>
        <Icon aria-label={label} />
        {children}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// ── Duration Badge (thumbnail overlay) ─────────────────────────────────────

export function DurationBadge({
  metadata,
  className
}: {
  readonly metadata: ItemMetadataLike;
  readonly className?: string;
}) {
  const duration = getMetaFieldAny(metadata, DURATION_FIELD_NAMES);
  if (!duration) {
    return null;
  }

  return (
    // Overlaid on the thumbnail itself, which is the one place the design system sanctions a raw
    // black/white pair. It lets the pointer through, so hovering it still reveals the media controls.
    <span className={cn('pointer-events-none bg-black/70 font-medium text-white', className)}>
      {duration}
    </span>
  );
}

// ── Key stats (followers / likes / saves / verified) ───────────────────────────

export type ItemKeyStatValues = {
  readonly followers: string | null;
  readonly likes: string | null;
  // A Pinterest pin's saves - the platform has no likes, so this stands in for them on Falcon.
  readonly saves: string | null;
  readonly verified: boolean;
  readonly verifiedMerchant: boolean;
};

// Followers falls back to the YouTube-style subscriber count so a channel reads the same on either
// platform.
export function getItemKeyStats(metadata: ItemMetadataLike): ItemKeyStatValues {
  return {
    followers: formatCompactNumber(
      getMetaFieldAny(metadata, FOLLOWER_FIELD_NAMES) ||
        getMetaFieldAny(metadata, SUBSCRIBER_FIELD_NAMES)
    ),
    likes: formatCompactNumber(getMetaFieldAny(metadata, LIKE_COUNT_FIELD_NAMES)),
    saves: formatCompactNumber(getMetaFieldAny(metadata, SAVE_COUNT_FIELD_NAMES)),
    verified: getMetaBoolean(metadata, VERIFIED_FIELD_NAMES),
    verifiedMerchant: getMetaBoolean(metadata, VERIFIED_MERCHANT_FIELD_NAMES)
  };
}

// Followers, likes and the verified flags as chips, for the part of a card that is always visible.
export function ItemKeyStats({metadata}: {readonly metadata: ItemMetadataLike}) {
  const {followers, likes, saves, verified, verifiedMerchant} = getItemKeyStats(metadata);
  if (!followers && !likes && !saves && !verified && !verifiedMerchant) {
    return null;
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      {followers ? (
        <StatChip label="Followers" icon={Users}>
          {followers}
        </StatChip>
      ) : null}
      {likes ? (
        <StatChip label="Likes" icon={ThumbsUp}>
          {likes}
        </StatChip>
      ) : null}
      {saves ? (
        <StatChip label="Saves" icon={Bookmark}>
          {saves}
        </StatChip>
      ) : null}
      {verified ? (
        <StatChip label={VERIFIED_LABEL} icon={BadgeCheck} variant="tag">
          Verified
        </StatChip>
      ) : null}
      {verifiedMerchant ? (
        <StatChip label="Verified merchant" icon={Store} variant="tag">
          Merchant
        </StatChip>
      ) : null}
    </div>
  );
}

// ── Metadata Pills Component ───────────────────────────────────────────────────
//
// Stats row (view/like count, language), a two-line description, a tag-pill
// row with overflow tooltip, and a collapsible "More details" section (full description, full
// tags, boolean flags, and any other metadata fields). Shared between the investigations grid/list
// views and the Weekly QA review modal.

export function ItemMeta({
  metadata,
  itemKey,
  title,
  omitKeyStats = false
}: {
  readonly metadata: ItemMetadataLike;
  // Analyst-facing key the translate-on-demand toggle is scoped to - shared with whichever sibling
  // component owns this item's title, so one click swaps title, description and tags together even
  // though only two of those three are rendered here.
  readonly itemKey: string;
  readonly title?: string | null;
  // The caller already shows audience size, likes and the verified flag above the fold (see
  // ItemKeyStats), so this row skips them rather than repeating them.
  readonly omitKeyStats?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const rawDescription = getDescription(metadata);
  const rawTags = getTags(metadata);
  const description = useTranslatedText(itemKey, rawDescription) ?? '';
  const tags = useTranslatedTexts(itemKey, rawTags);
  // Paired with the untranslated source tag for a React key: translation makes two distinct source
  // tags more likely to land on the same English word, so keying pills on the (post-translation)
  // display text risks a collision the original source text essentially never has.
  const tagPairs = rawTags.map((raw, i) => ({raw, display: tags[i]!}));
  const visible = tagPairs.slice(0, 3);
  const overflow = tagPairs.slice(3);

  const viewCount = formatCompactNumber(getMetaFieldAny(metadata, VIEW_COUNT_FIELD_NAMES));
  const likeCount = omitKeyStats
    ? null
    : formatCompactNumber(getMetaFieldAny(metadata, LIKE_COUNT_FIELD_NAMES));
  const commentCount = formatCompactNumber(getMetaFieldAny(metadata, COMMENT_COUNT_FIELD_NAMES));
  const category = getMetaFieldAny(metadata, CATEGORY_FIELD_NAMES);
  const language = getMetaFieldAny(metadata, LANGUAGE_FIELD_NAMES);
  // Channel-only - a video item never carries these, so no content-type branch is needed here.
  const subscribers = omitKeyStats
    ? null
    : formatCompactNumber(getMetaFieldAny(metadata, SUBSCRIBER_FIELD_NAMES));
  // Pinterest's (Falcon's) profile followers and pin saves - known fields, so they'd otherwise
  // vanish from "More details" wherever there are no key-stat chips to show them.
  const followers = omitKeyStats
    ? null
    : formatCompactNumber(getMetaFieldAny(metadata, FOLLOWER_FIELD_NAMES));
  const saves = omitKeyStats
    ? null
    : formatCompactNumber(getMetaFieldAny(metadata, SAVE_COUNT_FIELD_NAMES));
  const channelVideoCount = formatCompactNumber(
    getMetaFieldAny(metadata, CHANNEL_VIDEO_COUNT_FIELD_NAMES)
  );
  const lastPostDate = getMetaFieldAny(metadata, LAST_POST_DATE_FIELD_NAMES);
  const hasStats = Boolean(
    viewCount ||
    likeCount ||
    saves ||
    commentCount ||
    category ||
    language ||
    subscribers ||
    followers ||
    channelVideoCount ||
    lastPostDate
  );

  const madeForKids = getMetaBoolean(metadata, MADE_FOR_KIDS_FIELD_NAMES);
  const safeForMinors = getMetaBoolean(metadata, SAFE_FOR_MINORS_FIELD_NAMES);
  const livestream = getMetaBoolean(metadata, LIVESTREAM_FIELD_NAMES);
  const mature = getMetaBoolean(metadata, MATURE_FIELD_NAMES);
  // Channel-only - a video item never carries these, so no content-type branch is needed here.
  const joinedDate = getMetaFieldAny(metadata, JOINED_DATE_FIELD_NAMES);
  const country = getMetaFieldAny(metadata, COUNTRY_FIELD_NAMES);
  const otherFields = getMetaFields(metadata).filter(
    (f) => !KNOWN_FIELD_NAMES.has(f.FieldName.toLowerCase())
  );
  const hasExpandable =
    Boolean(description) ||
    tags.length > 0 ||
    Boolean(joinedDate) ||
    Boolean(country) ||
    madeForKids ||
    safeForMinors ||
    livestream ||
    mature ||
    otherFields.length > 0;

  const canTranslate =
    Boolean(title?.trim()) || Boolean(rawDescription.trim()) || rawTags.length > 0;
  if (!description && tags.length === 0 && !hasStats && !hasExpandable && !canTranslate) {
    return null;
  }

  return (
    <div className="border-border mt-3 space-y-2 border-t pt-3">
      <TranslateToggle itemKey={itemKey} texts={[title, rawDescription, ...rawTags]} />

      {hasStats ? (
        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
          {viewCount ? (
            <span className="flex items-center gap-1">
              <Eye className="size-3" />
              {viewCount}
            </span>
          ) : null}
          {likeCount ? (
            <span className="flex items-center gap-1">
              <ThumbsUp className="size-3" />
              {likeCount}
            </span>
          ) : null}
          {commentCount ? (
            <span className="flex items-center gap-1">
              <MessageSquare className="size-3" />
              {commentCount}
            </span>
          ) : null}
          {category ? <Badge variant="outline">{category}</Badge> : null}
          {saves ? (
            <span className="flex items-center gap-1">
              <Bookmark className="size-3" />
              {saves} saves
            </span>
          ) : null}
          {subscribers ? (
            <span className="flex items-center gap-1">
              <Users className="size-3" />
              {subscribers} subscribers
            </span>
          ) : null}
          {followers ? (
            <span className="flex items-center gap-1">
              <Users className="size-3" />
              {followers} followers
            </span>
          ) : null}
          {channelVideoCount ? (
            <span className="flex items-center gap-1">
              <Film className="size-3" />
              {channelVideoCount} videos
            </span>
          ) : null}
          {lastPostDate ? (
            <span className="flex items-center gap-1">
              <Clock className="size-3" />
              Last post {lastPostDate}
            </span>
          ) : null}
          {language ? (
            <Badge variant="outline" className="uppercase">
              {language}
            </Badge>
          ) : null}
        </div>
      ) : null}

      {description ? (
        <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">{description}</p>
      ) : null}

      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {visible.map((pair) => (
            <Badge key={pair.raw} variant="tag">
              {pair.display}
            </Badge>
          ))}
          {overflow.length > 0 ? (
            <Tooltip>
              <TooltipTrigger
                render={<Badge variant="tag" className="cursor-default font-semibold" />}>
                +{overflow.length}
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="flex flex-wrap gap-1">
                  {overflow.map((pair) => (
                    <span key={pair.raw}>{pair.display}</span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      {hasExpandable ? (
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="text-muted-foreground -ml-2 self-start"
              />
            }>
            <ChevronRight className="transition-transform in-data-panel-open:rotate-90" />
            {expanded ? 'Less' : 'More details'}
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="border-border space-y-2 rounded-lg border p-2">
              {description ? (
                <p className="text-foreground text-xs leading-relaxed">{description}</p>
              ) : null}

              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tagPairs.map((pair) => (
                    <Badge key={pair.raw} variant="tag">
                      {pair.display}
                    </Badge>
                  ))}
                </div>
              ) : null}

              {madeForKids || safeForMinors || livestream || mature ? (
                <div className="text-muted-foreground flex flex-wrap gap-2 text-xs font-medium">
                  {madeForKids ? <span>Made for kids</span> : null}
                  {safeForMinors ? <span>Safe for minors</span> : null}
                  {livestream ? <span>Livestream</span> : null}
                  {mature ? <span className="text-destructive-ink">Mature content</span> : null}
                </div>
              ) : null}

              {joinedDate || country ? (
                <div className="text-muted-foreground flex flex-wrap gap-2 text-xs">
                  {joinedDate ? <span>Joined {joinedDate}</span> : null}
                  {country ? <span>{country}</span> : null}
                </div>
              ) : null}

              {otherFields.length > 0 ? (
                <div className="text-muted-foreground space-y-0.5 text-xs">
                  {otherFields.slice(0, 8).map((f) => (
                    <div key={f.FieldName} className="flex justify-between gap-2">
                      <span className="shrink-0 font-medium">{f.FieldName}</span>
                      <span className="truncate text-right">{String(f.Content ?? '')}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
